import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  LinearProgress,
  Tooltip,
  Link
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SearchComponent from '../../components/common/SearchComponent';
import { fetchCustomerRequests } from '../../services/dataService';
import { searchProjects } from '../../services/searchService';
import usePageTitle from '../../utils/usePageTitle';
import '../../styles/printStyles.css';

const CustomerRequest = () => {
  usePageTitle('Customer Requests');
  const navigate = useNavigate();
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [currentSearchCriteria, setCurrentSearchCriteria] = useState(null);

  const handleAddCustomerRequest = () => {
    navigate('/add-customer-request-form');
  };

  const formatResults = (results) => {
    return results.map((item, index) => {
      const modelName = item.modelName || item.model_name || '';
      const allModelNames = item.allModelNames || item.all_model_names || item.models || 
                           item.model_names || item.modelNames || '';
      return {
        id: item.id || item.projectCode || item.project_code || index,
        projectCode: item.projectCode || item.project_code,
        budgetCode: item.budgetCode || item.budget_code,
        requestorName: item.requestorName || item.requestor_name,
        personInCharge: item.personInCharge || item.person_in_charge,
        modelName: modelName,
        allModelNames: allModelNames,
        projectStatus: item.projectStatus || item.project_status,
        location: item.location || item.project_location || item.loc || 
                 (item.location_code === 'C' ? 'Carson' : 
                  item.location_code === 'Q' ? 'QA' : 
                  item.location_code === 'P' ? 'PSA' : 
                  item.location_code || 'Carson')
      };
    });
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const pageParams = { page: paginationModel.page + 1, limit: paginationModel.pageSize };
      let data;

      if (currentSearchCriteria) {
        data = await searchProjects({ ...currentSearchCriteria, ...pageParams });
      } else {
        data = await fetchCustomerRequests({ ...pageParams, projectStatus: 'New' });
      }

      const results = Array.isArray(data) ? data : (data?.results || data?.data || []);
      const pagination = data?.pagination;
      
      setFilteredRows(formatResults(results));
      setTotalCount(pagination?.totalItems || results.length);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data';
      setError(`Error: ${errorMessage}`);
      setFilteredRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [paginationModel.page, paginationModel.pageSize, currentSearchCriteria]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = (searchCriteria) => {
    setCurrentSearchCriteria(searchCriteria);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  };

  const handleReset = () => {
    setCurrentSearchCriteria(null);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setError(null);
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';
    const statusUpper = status.toUpperCase();
    
    // Handle project status from stored function
    if (statusUpper === 'WIP') return 'WIP';
    if (statusUpper === 'COMPLETED') return 'Completed';
    if (statusUpper === 'NEW') return 'New';
    
    // Handle database status codes
    switch (statusUpper) {
      case 'ACT':
        return 'Active';
      case 'TER':
        return 'Terminated';
      case 'CLO':
        return 'Closed';
      case 'INV':
        return 'Invoiced';
      case 'APP':
        return 'Approved';
      case 'INS':
        return 'Ready for Invoice';
      default:
        return status;
    }
  };

  const parseModelNames = (allModelNames) => {
    if (!allModelNames) return [];
    
    // Decode HTML entities first
    const decoded = String(allModelNames)
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    
    // Split by various delimiters
    return decoded
      .split(/[&;,]/)
      .map(model => model.trim())
      .filter(model => model.length > 0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Customer Request Management
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleAddCustomerRequest}
          aria-label="Add new customer request form"
        >
          Add Customer Request Form
        </Button>
      </Box>

      {loading && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      <SearchComponent
        onSearch={handleSearch}
        onReset={handleReset}
        title="Search Criteria"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && filteredRows && Array.isArray(filteredRows) && (
        <Paper sx={{ width: '100%', mb: 2, boxShadow: 2, borderRadius: 2 }}>
          <TableContainer component={Paper} aria-label="Customer requests table">
            <Table sx={{ minWidth: 750 }} aria-label="Customer requests data">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Project Code</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Budget Code</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Requestor Name</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Person In Charge</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Model Name</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Project Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row, index) => (
                  <TableRow 
                    key={row.id || row.projectCode || index}
                    hover
                    sx={{
                      '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#e3f2fd !important' }
                    }}
                  >
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>
                      <Link
                        component={RouterLink}
                        to={`/customer-request/${row.projectCode}`}
                        aria-label={`View details for project ${row.projectCode}`}
                        sx={{ 
                          color: '#0d47a1',
                          textDecorationThickness: '2px',
                          fontSize: '13px',
                          '&:hover': { color: '#0a3d91' },
                          '&:focus-visible': {
                            outline: '3px solid #000000',
                            outlineOffset: '2px',
                            borderRadius: '2px',
                          }
                        }}
                      >
                        {row.projectCode || ''}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>{row.budgetCode || ''}</TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>{row.requestorName || ''}</TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>{row.personInCharge || ''}</TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>
                      {row.allModelNames ? (
                        <>
                          <Tooltip 
                            title={
                              <div>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>All Models for {row.projectCode}:</div>
                                {parseModelNames(row.allModelNames).map((model, idx) => (
                                  <div key={idx} style={{ marginBottom: '2px' }}>
                                    • {model}
                                  </div>
                                ))}
                              </div>
                            }
                            placement="top"
                            arrow
                          >
                            <span 
                              style={{ 
                                cursor: 'help', 
                                textDecoration: 'underline dotted',
                                display: 'inline-block'
                              }}
                              className="model-display"
                            >
                              {row.modelName || `${parseModelNames(row.allModelNames).length} Models`}
                            </span>
                          </Tooltip>
                          <div className="print-only" style={{ display: 'none' }}>
                            {parseModelNames(row.allModelNames).join(', ')}
                          </div>
                        </>
                      ) : (
                        <span className="model-display" style={{ color: '#666', fontStyle: 'italic' }}>
                          {row.modelName || 'No models assigned'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>{getStatusLabel(row.projectStatus)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={paginationModel.pageSize}
            page={paginationModel.page}
            onPageChange={(event, newPage) => {
              setPaginationModel({ ...paginationModel, page: newPage });
            }}
            onRowsPerPageChange={(event) => {
              setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) });
            }}
            sx={{
              backgroundColor: '#f8f9fa',
              borderTop: '2px solid #e0e0e0',
              '& .MuiTablePagination-root': { color: '#666' }
            }}
          />
        </Paper>
      )}
      
      {loading && (
        <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      )}

      {initialized && !loading && Array.isArray(filteredRows) && filteredRows.length === 0 && !error && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No results found. Please adjust your search criteria.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CustomerRequest;